import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  Alert,
  Chip,
  IconButton,
  TextField,
  CircularProgress
} from '@mui/material';
import {
  CloudQueue,
  ContentCopy,
  CheckCircle
} from '@mui/icons-material';

const steps = [
  {
    label: 'Create IAM Role',
    description: 'Create a cross-account IAM role for secure access',
  },
  {
    label: 'Attach Policies',
    description: 'Attach required AWS managed policies for comprehensive access',
  },
  {
    label: 'Configure Trust Relationship',
    description: 'Set up trust relationship with external ID for security',
  },
  {
    label: 'Complete Setup',
    description: 'Enter the Role ARN to complete the setup',
  },
];

const policies = [
  'ReadOnlyAccess',
  'AWSSupportAccess',
  'AWSConfigUserAccess',
  'SecurityAudit',
  'AWSCloudFormationReadOnlyAccess',
  'AWSCostAndBillingReadOnlyAccess'
];

function AWSAccountSetup({ open, onClose, onSetupLater, onAccountAdded }) {
  const [activeStep, setActiveStep] = useState(0);
  const [copiedText, setCopiedText] = useState('');
  const [accountName, setAccountName] = useState('');
  const [roleArn, setRoleArn] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [externalId] = useState(() => 'shellkode-' + Math.random().toString(36).substr(2, 9));
  const accountId = process.env.REACT_APP_PLATFORM_AWS_ACCOUNT_ID || '204836358848';

  const trustPolicy = {
    "Version": "2012-10-17",
    "Statement": [
      {
        "Effect": "Allow",
        "Principal": {
          "AWS": `arn:aws:iam::${accountId}:root`
        },
        "Action": "sts:AssumeRole",
        "Condition": {
          "StringEquals": {
            "sts:ExternalId": externalId
          }
        }
      }
    ]
  };

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(''), 2000);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleCompleteSetup = async () => {
    if (!accountName.trim() || !roleArn.trim()) {
      setError('Please provide both account name and role ARN');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/aws/accounts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          accountName: accountName.trim(),
          roleArn: roleArn.trim(),
          externalId: externalId,
          region: 'us-east-1'
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Account added successfully
        if (onAccountAdded) {
          onAccountAdded(data.account);
        }
        onClose();
      } else {
        setError(data.error || 'Failed to add AWS account');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <CloudQueue color="primary" />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              AWS Account Setup
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Connect your AWS account for comprehensive automation
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          <Typography variant="body2">
            <strong>Required Permissions:</strong> This setup provides read-only access to your entire AWS account 
            including billing information. No resources will be modified without your explicit consent.
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {step.label}
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {step.description}
                </Typography>

                {index === 0 && (
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        1. Go to AWS IAM Console → Roles → Create Role
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        • Select "Another AWS account"
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        • Account ID: <Chip label={accountId} size="small" />
                        <IconButton size="small" onClick={() => handleCopy(accountId, 'Account ID')}>
                          <ContentCopy fontSize="small" />
                        </IconButton>
                        {copiedText === 'Account ID' && <Chip label="Copied!" size="small" color="success" />}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        • External ID: <Chip label={externalId} size="small" />
                        <IconButton size="small" onClick={() => handleCopy(externalId, 'External ID')}>
                          <ContentCopy fontSize="small" />
                        </IconButton>
                        {copiedText === 'External ID' && <Chip label="Copied!" size="small" color="success" />}
                      </Typography>
                      <Typography variant="body2">
                        • Role Name: <strong>StartupAssistProRole</strong>
                      </Typography>
                    </CardContent>
                  </Card>
                )}

                {index === 1 && (
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        2. Attach the following AWS managed policies:
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        {policies.map((policy) => (
                          <Box key={policy} display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                            <CheckCircle color="success" fontSize="small" />
                            <Typography variant="body2">{policy}</Typography>
                            <IconButton size="small" onClick={() => handleCopy(policy, policy)}>
                              <ContentCopy fontSize="small" />
                            </IconButton>
                            {copiedText === policy && <Chip label="Copied!" size="small" color="success" />}
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {index === 2 && (
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        3. Update Trust Relationship
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Replace the trust policy with the following JSON:
                      </Typography>
                      <Box 
                        sx={{ 
                          backgroundColor: 'grey.100', 
                          p: 2, 
                          borderRadius: 1, 
                          position: 'relative',
                          fontFamily: 'monospace',
                          fontSize: '0.8rem',
                          maxHeight: 200,
                          overflow: 'auto'
                        }}
                      >
                        <IconButton 
                          size="small" 
                          sx={{ position: 'absolute', top: 8, right: 8 }}
                          onClick={() => handleCopy(JSON.stringify(trustPolicy, null, 2), 'Trust Policy')}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                        <pre>{JSON.stringify(trustPolicy, null, 2)}</pre>
                      </Box>
                      {copiedText === 'Trust Policy' && (
                        <Alert severity="success" sx={{ mt: 1 }}>
                          Trust policy copied to clipboard!
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                )}

                {index === 3 && (
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        4. Complete the Setup
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <TextField
                          fullWidth
                          label="Account Name"
                          value={accountName}
                          onChange={(e) => setAccountName(e.target.value)}
                          placeholder="e.g., Production Account"
                          sx={{ mb: 2 }}
                          disabled={isSubmitting}
                        />
                        <TextField
                          fullWidth
                          label="Role ARN"
                          value={roleArn}
                          onChange={(e) => setRoleArn(e.target.value)}
                          placeholder="arn:aws:iam::YOUR-ACCOUNT:role/StartupAssistProRole"
                          disabled={isSubmitting}
                          helperText="Copy the Role ARN from the AWS IAM console after creating the role"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                )}

                <Box sx={{ mb: 1 }}>
                  <div>
                    {index === steps.length - 1 ? (
                      <Button
                        variant="contained"
                        onClick={handleCompleteSetup}
                        sx={{ mt: 1, mr: 1 }}
                        disabled={isSubmitting || !accountName.trim() || !roleArn.trim()}
                        startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
                      >
                        {isSubmitting ? 'Adding Account...' : 'Complete Setup'}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={handleNext}
                        sx={{ mt: 1, mr: 1 }}
                      >
                        Continue
                      </Button>
                    )}
                    <Button
                      disabled={index === 0 || isSubmitting}
                      onClick={handleBack}
                      sx={{ mt: 1, mr: 1 }}
                    >
                      Back
                    </Button>
                  </div>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>

        {activeStep === steps.length && (
          <Card sx={{ mt: 2 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircle color="success" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Setup Complete!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your AWS account is now ready for comprehensive automation and monitoring.
              </Typography>
            </CardContent>
          </Card>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={onSetupLater} color="inherit" disabled={isSubmitting}>
          Setup Later
        </Button>
        <Button onClick={onClose} variant="contained" disabled={isSubmitting}>
          {activeStep === steps.length ? 'Get Started' : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AWSAccountSetup;